import { Component, forwardRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DialogueTexts } from '../../constants/dialog-texts.constant';

@Component({
  selector: 'app-month-picker',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatIconModule],
  template: `
    <div class="month-picker-container">
      <button
        type="button"
        class="month-picker-button"
        (click)="togglePicker()"
        [attr.aria-expanded]="showPicker"
      >
        <span class="selected-month" [class.placeholder]="displayMonth === 'Select Month'">{{
          displayMonth
        }}</span>
        <mat-icon>calendar_today</mat-icon>
      </button>

      <div class="month-picker-dropdown" *ngIf="showPicker" (click)="$event.stopPropagation()">
        <div class="picker-header">
          <button
            type="button"
            (click)="previousYear()"
            [disabled]="isPreviousYearDisabled"
            class="nav-btn"
          >
            <mat-icon>chevron_left</mat-icon>
          </button>
          <div class="year-selector">
            <button type="button" (click)="toggleYearDropdown()" class="year-display">
              {{ currentYear }}
              <mat-icon class="dropdown-icon">expand_more</mat-icon>
            </button>
            <div class="year-dropdown" *ngIf="showYearDropdown">
              <div class="year-list">
                <button
                  type="button"
                  *ngFor="let year of years"
                  class="year-option"
                  [class.selected]="year === currentYear"
                  (click)="selectYear(year)"
                >
                  {{ year }}
                </button>
              </div>
            </div>
          </div>
          <button
            type="button"
            (click)="nextYear()"
            [disabled]="isNextYearDisabled"
            class="nav-btn"
          >
            <mat-icon>chevron_right</mat-icon>
          </button>
        </div>

        <div class="months-grid">
          <button
            type="button"
            *ngFor="let month of months"
            class="month-btn"
            [disabled]="month.disabled"
            [class.disabled]="month.disabled"
            [class.selected]="isMonthSelected(month)"
            (click)="!month.disabled && selectMonth(month)"
          >
            {{ month.short }}
          </button>
        </div>

        <div class="picker-actions">
          <button type="button" class="aecom_btn" (click)="cancel()">
            {{ dialogTexts.REPORT_MONTH.CLOSE }}
          </button>
          <button
            type="button"
            class="aecom_btn--solid"
            (click)="confirm()"
            [disabled]="!tempMonth"
          >
            {{ dialogTexts.REPORT_MONTH.CONFIRM }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .month-picker-container {
        position: relative;
        width: 100%;
        &:hover {
          border-color: var(--brand);
        }
      }

      .month-picker-button {
        width: 100%;
        height: 40px;
        padding: 10px 12px;
        border: 1px solid #ccc;
        border-radius: 6px;
        background: white;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 14px;
        transition: border-color 0.3s;

        &:hover {
          border-color: var(--brand);
        }

        &:focus {
          border: 1px solid var(--brand);
        }
      }

      .selected-month {
        flex: 1;
        text-align: left;
      }

      .selected-month.placeholder {
        color: #9d9c9c !important;
        font-family: 'AECOMSans';
        font-weight: 400;
        font-size: 14px;
      }

      mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: #666;
      }

      .month-picker-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ccc;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        z-index: 1000;
        margin-top: 8px;
        padding: 16px;
      }

      .picker-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #eee;
        gap: 8px;
      }

      .year-selector {
        position: relative;
        flex: 1;
        text-align: center;
      }

      .year-display {
        background: none;
        border: 1px solid #ddd;
        padding: 6px 12px;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        width: 100%;
        font-weight: 500;
        font-size: 14px;
        transition: all 0.2s;

        &:hover {
          border-color: #008768;
          background-color: #f5f5f5;
        }
      }

      .dropdown-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        position: absolute;
        right: 5px;
      }

      .year-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ddd;
        border-radius: 6px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        z-index: 1001;
        margin-top: 4px;
        max-height: 250px;
        overflow-y: auto;
      }

      .year-list {
        display: flex;
        flex-direction: column;
      }

      .year-option {
        padding: 8px 12px;
        border: none;
        background: white;
        text-align: center;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;

        &:hover {
          background-color: #f5f5f5;
          color: #008768;
        }

        &.selected {
          background-color: #e3f2fd;
          color: #008768;
          font-weight: 600;
        }
      }

      .nav-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        transition: background-color 0.2s;

        &:hover {
          background-color: #f0f0f0;
        }
      }

      .nav-btn:disabled {
        cursor: not-allowed;
        &:hover {
          background-color: #fffefe;
        }
      }

      .months-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
        margin-bottom: 16px;
      }

      .month-btn {
        padding: 8px;
        border: 1px solid #ddd;
        border-radius: 6px;
        background: white;
        cursor: pointer;
        font-size: 15px;
        transition: all 0.2s;
        border-radius: 6px;

        &:hover {
          border-color: #008768;
          background-color: #f5f5f5;
        }

        &.selected {
          background-color: #008768;
          color: white;
          border-color: #008768;
        }
      }

      .month-btn:disabled,
      .month-btn.disabled {
        cursor: not-allowed;
      }

      .picker-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        padding-top: 12px;
        border-top: 1px solid #eee;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthPickerComponent),
      multi: true,
    },
  ],
})
export class MonthPickerComponent implements ControlValueAccessor {
  @Input() placeholder = 'Select Month';

  showPicker = false;
  currentYear: number = new Date().getFullYear();
  actualCurrentYear = new Date().getFullYear();
  actualCurrentMonth = new Date().getMonth() + 1;
  startYear = 1990;
  showYearDropdown = false;
  tempMonth: string | null = null;
  selectedMonth: string | null = null;
  displayMonth = 'Select Month';
  dialogTexts = DialogueTexts;

  years = Array.from(
    { length: this.currentYear - this.startYear + 1 },
    (_, i) => this.currentYear - i,
  );

  ngOnInit() {
    this.updateDisabledMonths();
  }

  months = [
    { short: 'Jan', full: 'January', value: '01', disabled: false },
    { short: 'Feb', full: 'February', value: '02', disabled: false },
    { short: 'Mar', full: 'March', value: '03', disabled: false },
    { short: 'Apr', full: 'April', value: '04', disabled: false },
    { short: 'May', full: 'May', value: '05', disabled: false },
    { short: 'Jun', full: 'June', value: '06', disabled: false },
    { short: 'Jul', full: 'July', value: '07', disabled: false },
    { short: 'Aug', full: 'August', value: '08', disabled: false },
    { short: 'Sep', full: 'September', value: '09', disabled: false },
    { short: 'Oct', full: 'October', value: '10', disabled: false },
    { short: 'Nov', full: 'November', value: '11', disabled: false },
    { short: 'Dec', full: 'December', value: '12', disabled: false },
  ];

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  togglePicker() {
    this.showPicker = !this.showPicker;
    if (this.showPicker) {
      this.tempMonth = this.selectedMonth;
      document.addEventListener('click', this.onClickOutside.bind(this));
    }
  }

  private onClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.month-picker-container')) {
      this.showPicker = false;
      this.showYearDropdown = false;
      document.removeEventListener('click', this.onClickOutside);
    }
  };

  toggleYearDropdown() {
    this.showYearDropdown = !this.showYearDropdown;
  }

  selectYear(year: number) {
    this.currentYear = Number(year);
    this.showYearDropdown = false;
    this.updateDisabledMonths();
  }

  get isPreviousYearDisabled(): boolean {
    return this.currentYear <= this.startYear;
  }

  previousYear() {
    if (!this.isPreviousYearDisabled) {
      this.currentYear--;
      this.updateDisabledMonths();
    }
  }

  get isNextYearDisabled(): boolean {
    return this.currentYear >= this.actualCurrentYear;
  }

  nextYear() {
    if (this.isNextYearDisabled) {
      return;
    }
    this.currentYear++;
    this.updateDisabledMonths();
  }

  selectMonth(month: any) {
    this.tempMonth = `${this.currentYear}-${month.value}`;
  }

  updateDisabledMonths(): void {
    this.months.forEach((month) => {
      const monthNo = Number(month.value);
      month.disabled =
        this.currentYear > this.actualCurrentYear ||
        (this.currentYear === this.actualCurrentYear && monthNo > this.actualCurrentMonth);
    });
  }

  isMonthSelected(month: any): boolean {
    if (!this.tempMonth) return false;
    return this.tempMonth === `${this.currentYear}-${month.value}`;
  }

  confirm() {
    if (this.tempMonth) {
      this.selectedMonth = this.tempMonth;
      this.updateDisplay();
      this.onChange(this.selectedMonth);
      this.showPicker = false;
      document.removeEventListener('click', this.onClickOutside);
    }
  }

  cancel() {
    this.showPicker = false;
    this.showYearDropdown = false;
    this.tempMonth = null;
    document.removeEventListener('click', this.onClickOutside);
  }

  private updateDisplay() {
    if (this.selectedMonth) {
      const [year, month] = this.selectedMonth.split('-');
      const monthObj = this.months.find((m) => m.value === month);
      this.displayMonth = `${monthObj?.full} ${year}`;
    }
  }

  writeValue(value: any): void {
    if (value === null || value === '') {
      this.selectedMonth = null;
      this.displayMonth = 'Select Month';
      this.updateDisabledMonths();
      return;
    }

    this.selectedMonth = value;
    this.updateDisabledMonths();
    this.updateDisplay();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
